import type { PropsWithChildren, ReactNode } from "react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDocumentViewport } from "@/features/document-viewport/context";
import { cn } from "@/lib/utils";

export function DocumentViewportToolbar({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div data-viewport-tools-ui className="pointer-events-none absolute inset-x-0 top-0 z-50 flex">
      <div
        className={cn(
          "pointer-events-auto flex h-12 w-full items-center gap-2 border-b border-border/70 bg-background/95 px-3 backdrop-blur",
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function DocumentViewportToolbarSection({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      data-viewport-tools-ui
      className={cn(
        "flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-2 py-1",
        className
      )}
    >
      {children}
    </div>
  );
}

export function DocumentViewportLeftRail({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute left-0 top-[49px] z-40 flex flex-col gap-0",
        className
      )}
    >
      {children}
    </div>
  );
}

export function DocumentViewportRailGroup({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        "pointer-events-auto relative flex flex-col gap-0",
        className
      )}
    >
      {children}
    </div>
  );
}

export function DocumentViewportPageControls({
  activePage,
  pageCount,
  centerLabel,
}: {
  activePage: number;
  pageCount: number;
  centerLabel?: ReactNode;
}) {
  const viewport = useDocumentViewport();

  // Generate page options for dropdown
  const pageOptions = Array.from({ length: pageCount }, (_, i) => i + 1);

  return (
    <div className="ml-auto flex items-center gap-1">
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        onClick={() => viewport.goToPage(activePage - 1)}
        disabled={activePage <= 1}
        aria-label="Previous page"
      >
        <ChevronLeft className="size-4" />
      </Button>
      {centerLabel ?? (
        <Select
          value={String(activePage)}
          onValueChange={(value) => viewport.goToPage(Number(value))}
        >
          <SelectTrigger
            className="h-8 w-20 text-xs font-medium tabular-nums"
            aria-label="Go to page"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[160]">
            {pageOptions.map((page) => (
              <SelectItem key={page} value={String(page)} className="text-xs">
                {page} / {pageCount}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        onClick={() => viewport.goToPage(activePage + 1)}
        disabled={activePage >= pageCount}
        aria-label="Next page"
      >
        <ChevronRight className="size-4" />
      </Button>
      <div className="mx-1 h-5 w-px bg-border" />
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        onClick={() =>
          viewport.scaleZoomAt(0.9, {
            x: viewport.workspaceSize.width / 2,
            y: viewport.workspaceSize.height / 2,
          })
        }
        aria-label="Zoom out"
      >
        <ZoomOut className="size-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        onClick={() =>
          viewport.scaleZoomAt(1.1, {
            x: viewport.workspaceSize.width / 2,
            y: viewport.workspaceSize.height / 2,
          })
        }
        aria-label="Zoom in"
      >
        <ZoomIn className="size-4" />
      </Button>
    </div>
  );
}
